import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../store/slices/authSlice';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector(s => s.auth);

  const [stats, setStats] = useState({ device_count: null, location_count: null, transition_count: null });
  useEffect(() => {
    fetch('/api/reports/public-stats/')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStats(d); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(login({ email, password }));
    if (login.fulfilled.match(result)) {
      const user = result.payload.user;
      localStorage.setItem('user', JSON.stringify(user));
      if (user?.role === 'BHEL_MANAGEMENT') navigate('/bhel/dashboard');
      else if (user?.role === 'BHEL_SPOC') navigate('/bhel/my-location');
      else navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex" style={{background: 'linear-gradient(135deg, #3D3562 0%, #272244 60%, #1C1831 100%)'}}>
      {/* Left branding panel */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-magenta-500/10" style={{background: 'rgba(193,39,141,0.1)'}} />
        <div className="absolute top-40 -left-10 w-48 h-48 rounded-full" style={{background: 'rgba(255,255,255,0.05)'}} />
        <div className="absolute -bottom-20 right-20 w-64 h-64 rounded-full" style={{background: 'rgba(193,39,141,0.1)'}} />
        <div className="absolute bottom-40 -right-10 w-32 h-32 rounded-full" style={{background: 'rgba(255,255,255,0.05)'}} />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-4">
          <svg viewBox="0 0 40 44" fill="none" className="w-12 h-12 flex-shrink-0">
            <path d="M20 2L38 40H2L20 2Z" fill="#C1278D"/>
            <circle cx="20" cy="17" r="4" fill="white"/>
            <rect x="17" y="20" width="6" height="10" rx="1" fill="white"/>
          </svg>
          <div>
            <p className="text-white text-2xl font-black tracking-tight">PROACTIVE™</p>
            <p className="text-purple-300 text-sm">Enhancing Digital Experience</p>
          </div>
        </div>

        {/* Center content */}
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-white leading-tight mb-4">
            BHEL Incident<br/>Management System
          </h2>
          <p className="text-purple-300 text-lg leading-relaxed max-w-sm">
            Track, manage, and resolve BHEL Video Conferencing incidents with full SLA visibility.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[[stats.device_count, 'Devices'], [stats.location_count, 'Locations'], [stats.transition_count, 'Transitions']].map(([n, l]) => (
              <div key={l} className="rounded-2xl p-4" style={{background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)'}}>
                <p className="text-white text-2xl font-bold">{n ?? '—'}</p>
                <p className="text-purple-300 text-sm">{l}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-purple-400 text-xs">© 2026 Proactive Data Systems Pvt. Ltd.</p>
      </div>

      {/* Right login form */}
      <div className="w-full lg:w-[460px] flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <svg viewBox="0 0 40 44" fill="none" className="w-10 h-10">
              <path d="M20 2L38 40H2L20 2Z" fill="#C1278D"/>
              <circle cx="20" cy="17" r="4" fill="white"/>
              <rect x="17" y="20" width="6" height="10" rx="1" fill="white"/>
            </svg>
            <div>
              <p className="text-brand-500 text-xl font-black">PROACTIVE™</p>
              <p className="text-gray-400 text-xs">Enhancing Digital Experience</p>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-brand-500 mb-1">Welcome back</h1>
          <p className="text-gray-400 text-sm mb-8">Sign in to your BHEL Ticketing account</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="input-field"
                placeholder="you@proactive.co.in"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 px-4 py-2.5 rounded-xl">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                {typeof error === 'string' ? error : 'Invalid email or password'}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center flex mt-6">
              {loading ? (
                <span className="flex items-center gap-2"><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Signing in...</span>
              ) : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
